import { push } from "connected-react-router";
import posthog from "posthog-js";
import { call, delay, put, retry, takeEvery } from "redux-saga/effects";

import * as Sentry from "@sentry/react";
import * as casesActions from "@store/cases/casesActions";
import * as questionsSlice from "@store/questionnaire/questionnaireSlice";
import { WEBSOCKET_SEND, WebSocketSend } from "@store/socket/socketActions";
import { SocketEvent, SocketEvents } from "@store/socket/socketChannel";
import { setSocketConnection } from "@store/socket/socketSaga";
import { PaymentManagerSetup, PaymentMethod, PaymentMethodManagerView } from "@types-runway/billing";
import { Case } from "@types-runway/case";
import { selectState } from "@store/selectState";
import { ErrorEvents } from "@types-runway/state";
import { User } from "@types-runway/user";
import { buildFileFormData } from "@utils/forms/formData";
import { setDepartureDate } from "@utils/analytics/klaviyo";
import { toastError, toastSuccess } from "@utils/utils";

import { auth, user as userRoutes } from "../../api";
import { clearMedicationsCache } from "../../hooks/case/useFetchMedications";
import * as casesSlice from "../cases/casesSlice";
import { clearMessages } from "../messages/messagesSlice";
import { clearPaymentMethodForm } from "../paymentMethodForm/paymentMethodFormSlice";
import { errorHandler } from "../ui/uiSlice";
import * as userActions from "./userActions";
import {
    addressesFetchSuccess, cleanUserError, clearUser, paymentMethodsFetchSuccess,
    promoCodesFetchSuccess, userError, userFetchSuccess, userMutationSuccess, userPending
} from "./userSlice";

function* login({ email, password }: userActions.Login) {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    const user: User = yield selectState<User>((state) => state.user.data);
    // Log the user.
    const { data } = yield call(() =>
      auth.authPost("login", {
        email,
        password,
      })
    );

    // Identify the user in PostHog
    posthog.identify(data.user.id, {
      email: email.toLowerCase(),
    });
    // Identify user in Sentry
    Sentry.setUser({ email: data.user.email });

    // Set the user in the app
    localStorage.setItem("token", data.token);
    yield put(
      userFetchSuccess({
        ...data.user,
        partnerTx: user?.partnerTx,
      })
    );
    // We populate user cases on login.
    yield put(casesSlice.getCasesSuccess(data.cases));
    // Sync up cases with MDI during login.
    yield put({ type: casesActions.REFRESH_CASES, userId: data.user.id });

    // Clear the error handler.
    yield put(errorHandler(""));
    // Finally, initialize web socket connection.
    yield call(setSocketConnection, data.token);
  } catch (e) {
    const exception: any = e;
    const event = exception?.response?.status === 500 ? ErrorEvents.SERVER_ERROR : undefined;
    const message = exception?.response?.data.message || event || "Unknown error.";
    yield put(
      userError({
        event,
        message,
      })
    );
  }
}
export function* logout({ userId, reason }: userActions.Logout) {
  try {
    yield put(cleanUserError());
    yield call(() => auth.authDelete("logout"));
    sessionStorage.clear();
    localStorage.removeItem("token");
    // Reset PostHog session
    posthog.reset();
    // Clear the user cases from the redux state before an app refresh.
    yield put(clearUser());
    yield put(casesSlice.clearCases());
    yield put(casesSlice.clearNewCase());
    yield put(questionsSlice.clearForm());
    yield put(clearPaymentMethodForm());
    yield put(clearMessages());
    clearMedicationsCache();
    // Disconnect socket.
    const event: SocketEvent<{}> = {
      eventName: SocketEvents.DISCONNECT,
      payload: {
        userId,
      },
    };
    const action: WebSocketSend<{}> = { type: WEBSOCKET_SEND, event };
    yield put(action);
    // Router state survives the sessionStorage.clear() above, so /login can explain why.
    yield put(reason ? push("/login", { logoutReason: reason }) : push("/login"));
  } catch (e) {
    Sentry.captureException(e);
    Sentry.captureMessage("A user is having trouble logging out.");
  }
}
function* signup({ user }: userActions.Signup) {
  try {
    const newCase: Case = yield selectState<Case>((state) => state.case.newCase.data);
    yield put(userPending());
    yield put(cleanUserError());
    // Set sentry user before changing component.
    Sentry.setUser({ email: user.email });
    const {
      data: { user: newUser },
    } = yield call(() => userRoutes.userPost("", { user }));
    // Once registered, log in user.
    yield put({
      type: userActions.LOGIN,
      email: user.email,
      password: user.password,
    });
    // if (newCase?.trip?.from) {
    //   setDepartureDate(user.first_name, user.email, newCase.trip.from);
    // }
  } catch (error) {
    const exception: any = error;
    const event = exception?.response?.status === 500 ? ErrorEvents.SERVER_ERROR : undefined;
    const message =
      exception?.response?.data?.message || exception?.response?.statusText || "Unknown error.";

    yield put(
      userError({
        event,
        message,
      })
    );
  }
}

export function* restorePassword({ email }: userActions.RestorePassword) {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    yield call(() =>
      auth.authPost("password/reset", {
        email,
      })
    );
    yield put(userMutationSuccess({}));
    toastSuccess("Please check your email.", "We've sent you a link to recover your password.");
    yield put(push("/login"));
  } catch (e) {
    let event: string | undefined;

    yield put(
      userError({
        event,
        message: "Could not restore password.",
      })
    );
  }
}

function* collectMarketingEmail({
  email,
  subscribeToNewsLetter,
  discountBody,
}: userActions.CollectMarketingEmail) {
  try {
    yield call(() =>
      userRoutes.userPost("collect-marketing-email", { email, subscribeToNewsLetter, discountBody })
    );
  } catch (e) {
    Sentry.captureException(e);
  }
}

function* userUpdate({ userUpdate }: userActions.UserUpdate) {
  try {
    const { data: serverResponse } = yield call(() => userRoutes.userPatch("profile", userUpdate));

    // Re-read user state after API call to avoid race conditions
    // The state may have been updated by other actions during the async call
    const currentUser: User = yield selectState<User>((state) => state.user.data);

    // Only merge fields from server response that were in the update request
    // This prevents overwriting fields (like paymentMethods) that the server
    // might return as empty/null due to endpoint scope limitations
    const updatedFields = Object.keys(userUpdate).reduce((acc, key) => {
      const typedKey = key as keyof typeof userUpdate;
      if (serverResponse && typedKey in serverResponse) {
        (acc as any)[typedKey] = (serverResponse as any)[typedKey];
      } else {
        (acc as any)[typedKey] = userUpdate[typedKey];
      }
      return acc;
    }, {} as Partial<User>);

    yield put(userMutationSuccess({ ...currentUser, ...updatedFields }));
  } catch (e) {
    const exception: any = e;
    let event: string | undefined;

    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }

    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* updateShippingAddress({ address }: userActions.UpdateShippingAddress) {
  try {
    const { data } = yield call(() => userRoutes.userPatch("shipping-address", { address }));
    yield put(userMutationSuccess(data));
    toastSuccess("Success!", "Your shipping address has been updated.");
  } catch (e) {
    toastError("Error!", "Could not update shipping address.");
    const exception: any = e;
    let event: string | undefined;

    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }

    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* createAddress({ address: addressPayload, userId }: userActions.CreateAddress) {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    let user: User = yield selectState<User>((state) => state.user.data);
    let newCase: Case = yield selectState<Case>((state) => state.case.newCase.data);
    const { data } = yield call(() =>
      // Hardcode country on address creation.
      userRoutes.userPost("address", { address: { ...addressPayload, country: "us" } })
    );
    // Assing address to user.
    yield call(() => userRoutes.userPut("address", { address: { id: data.id, UserId: userId } }));

    let newAddressesArray = [];

    if (user.addresses) {
      newAddressesArray = [
        ...user.addresses.map((address) => {
          if (addressPayload.default) {
            return {
              ...address,
              default: false,
            };
          }
          return address;
        }),
        data,
      ];
    } else {
      newAddressesArray.push(data);
    }
    yield put(userMutationSuccess({ ...user, addresses: newAddressesArray }));
    toastSuccess("Success!", "Address created.");

    if (!user["patient-id"]) {
      // Create patient profile in background
      // Assing new case to user.
      yield put({
        type: userActions.CREATE_PATIENT_PROFILE,
        userId: user.id,
      });
      // Set shipping address to case as this is the first added address.
      yield put({
        type: casesActions.UPDATE_CASE,
        caseId: newCase.id,
        isAuth: true,
        updateBody: {
          shippingAddress: {
            id: data.id,
          },
        },
      });
    }
  } catch (e) {
    toastError("Error.", "Failed creating user addresses");
    const exception: any = e;
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* createPatientProfile({ userId }: userActions.CreatePatientProfile) {
  const user: User = yield selectState<User>((state) => state.user.data);
  try {
    const { data: updatedUser } = yield retry(
      3,
      3000,
      userRoutes.userPost,
      `${userId}/patient-profile`,
      {}
    );
    yield put(
      userMutationSuccess({
        ...user,
        "patient-id": updatedUser["patient-id"],
      })
    );
  } catch (error) {
    Sentry.captureException(error);
  }
}

export function* submitPhotoId({ file }: userActions.SubmitPhotoId) {
  try {
    yield put(userPending());
    yield put(cleanUserError());

    const currentUser: User = yield selectState<User>((state) => state.user.data);

    const formData = buildFileFormData(file, "photo-id");
    const { data: fileKey } = yield call(() => userRoutes.userPost("photo-id", formData));

    const { data: updatedUser } = yield call(() =>
      userRoutes.userPatch("profile", { driver_license_id: fileKey })
    );

    // Merge onto current state rather than replacing it outright — PATCH
    // /user/profile's response can never include client-only fields like
    // driver_license_case (ParentalConsent's guardian-ID case marker, set by
    // the unrelated guardian-consent flow), so a bare $set here would
    // silently wipe it out from under a still-open guardian-consent flow in
    // the same tab (ENG-618).
    const mergedUser = { ...currentUser, ...updatedUser };

    // Flips the visible state to "in review" — must not wait on MDI delivery below.
    yield put(userMutationSuccess(mergedUser));

    try {
      const { data: confirmedFileId } = yield call(() =>
        userRoutes.userPost(`${updatedUser.id}/photo-id/sync`, {})
      );
      // MDI confirmed delivery synchronously — reflect it immediately so the
      // patient doesn't have to log out and back in to see "verified".
      if (confirmedFileId) {
        yield put(userMutationSuccess({ ...mergedUser, driver_license_id: confirmedFileId }));
      }
    } catch (syncError) {
      // Best-effort delivery: the patient still sees "in review" either way.
      Sentry.captureException(syncError);
    }
  } catch (e) {
    const exception: any = e;
    let event: string | undefined;
    let message = "Something went wrong while submitting your ID. Please try again.";

    if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
      message = "That file couldn't be uploaded. Please choose a different one and try again.";
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
      message = "Please login again to continue...";
    }

    yield put(
      userError({
        event,
        message,
      })
    );
  }
}

function* updateAddress({ payload, addressId }: userActions.UpdateAddress) {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    const user: User = yield selectState<User>((state) => state.user.data);
    const { data } = yield call(() => userRoutes.userPut("address", { address: payload }));

    let updatedAddresses = [
      ...user.addresses.filter((address) => address.id !== addressId),
      data,
    ].map((address) => {
      if (payload.default) {
        return {
          ...address,
          default: address.id === addressId ? true : false,
        };
      }

      return address;
    });

    yield put(userMutationSuccess({ ...user, addresses: updatedAddresses }));
    toastSuccess("Success!", "Address updated.");
  } catch (e) {
    toastError("Error.", "Failed updating user addresses");
    const exception: any = e;
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* deleteAddress({ addressId }: userActions.DeleteAddress) {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    const user: User = yield selectState<User>((state) => state.user.data);
    yield call(() => userRoutes.userDelete(`address/${addressId}`));

    // Remove address from user.
    const updatedAddresses = user.addresses.filter((address) => address.id !== addressId);

    yield put(userMutationSuccess({ ...user, addresses: updatedAddresses }));
  } catch (e) {
    toastError("Error.", "Failed deleting user addresses");
    const exception: any = e;
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* getAddresses({ userId }: userActions.GetAddresses) {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    const { data } = yield call(() => userRoutes.userGet(`${userId}/addresses`));
    yield put(addressesFetchSuccess(data));
  } catch (e) {
    const exception: any = e;
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* getPaymentMethods() {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    const { data } = yield call(() => userRoutes.userGet("payment-methods"));
    yield put(paymentMethodsFetchSuccess(data));
  } catch (e) {
    toastError("Error.", "Could not get payment methods.");
    const exception: any = e;
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* addPaymentMethod({ paymentMethodId, view }: userActions.AddPaymentMethod) {
  yield put(userPending());
  yield put(cleanUserError());
  try {
    const user: User = yield selectState<User>((state) => state.user.data);
    const { data } = yield call(() =>
      userRoutes.userPost("payment-method", {
        paymentMethodId,
      })
    );
    const paymentMethods: PaymentMethod[] = [...user.paymentMethods, data];
    yield put(userMutationSuccess({ ...user, paymentMethods }));
    yield put({ type: userActions.ADD_PAYMENT_METHOD_SUCCEEDED, paymentMethodId });

    // Set default after adding it.
    yield put({
      type: userActions.SET_DEFAULT_PAYMENT_METHOD,
      methodId: paymentMethodId,
      view,
    });

    if (view === PaymentMethodManagerView.PROFILE_SETTINGS) {
      toastSuccess("Success!", "New payment method added.");
    }
  } catch (e) {
    const exception: any = e;
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }

    const errorMessage = exception?.response?.data?.message;
    toastError("Error.", errorMessage);
    yield put(
      userError({
        event,
        message: errorMessage,
      })
    );
    yield put({
      type: userActions.ADD_PAYMENT_METHOD_FAILED,
      paymentMethodId,
      message: errorMessage,
    });
  }
}

function* updatePaymentMethod({ paymentMethodUpdate }: userActions.UpdatePaymentMethod) {
  yield put(userPending());
  yield put(cleanUserError());
  try {
    const user: User = yield selectState<User>((state) => state.user.data);
    const { data } = yield call(() =>
      userRoutes.userPut("payment-method", { paymentMethod: paymentMethodUpdate })
    );
    const paymentMethods: PaymentMethod[] = [...user.paymentMethods];

    const updatedPaymentMethodIndex = paymentMethods.findIndex(
      (method) => method.id === paymentMethodUpdate.id
    );

    paymentMethods[updatedPaymentMethodIndex] = data;

    yield put(userMutationSuccess({ ...user, paymentMethods }));

    // Set default after adding it.
    yield put({
      type: userActions.SET_DEFAULT_PAYMENT_METHOD,
      methodId: paymentMethodUpdate.id,
      managerSetup: PaymentManagerSetup.EDIT,
    });
    toastSuccess("Success!", "Payment method updated.");
  } catch (e) {
    const exception: any = e;
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* setDefaultPaymentMethod({ methodId, view }: userActions.SetDefaultPaymentMethod) {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    const user: User = yield selectState<User>((state) => state.user.data);
    yield call(() => userRoutes.userPatch(`payment-method/${methodId}/default`));
    yield put(userMutationSuccess({ ...user, defaultPaymentMethod: methodId }));
    if (view === PaymentMethodManagerView.PROFILE_SETTINGS) {
      toastSuccess("Success!", "Preferred payment method updated.");
    }
  } catch (e) {
    const exception: any = e;
    toastError("Error.", "Could not set payment method as default.");
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

export function* deletePaymentMethods({ methodIds }: userActions.DeletePaymentMethods) {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    const user: User = yield selectState<User>((state) => state.user.data);

    const results: PromiseSettledResult<string>[] = yield call(() =>
      Promise.allSettled(
        methodIds.map((methodId) =>
          userRoutes.userDelete(`payment-method/${methodId}`).then(() => methodId)
        )
      )
    );

    const removedIds = results
      .filter((result): result is PromiseFulfilledResult<string> => result.status === "fulfilled")
      .map((result) => result.value);

    const paymentMethods = user.paymentMethods.filter(
      (method) => !removedIds.includes(method.id)
    );
    yield put(userMutationSuccess({ ...user, paymentMethods }));

    if (removedIds.length < methodIds.length) {
      toastError("Error.", "Some payment methods could not be removed. Please try again.");
    } else {
      toastSuccess(
        "Success!",
        removedIds.length > 1 ? "Payment methods removed." : "Payment method removed."
      );
    }
  } catch (e) {
    const exception: any = e;
    toastError("Error.", "Could not delete payment method.");
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* sendTravelGroupEmail({ emails }: userActions.SendTravelGroupEmail) {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    yield call(() =>
      userRoutes.userPost("referrals", {
        referrals: {
          emails,
          type: "travel_group",
        },
      })
    );
    const user: User = yield selectState<User>((state) => state.user.data);
    yield put(userFetchSuccess({ ...user }));
  } catch (e) {
    const exception: any = e;
    toastError("Error.", "Could not refer travel group.");
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    } else if (exception?.message === ErrorEvents.ERR_NETWORK) {
      event = ErrorEvents.ERR_NETWORK;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* getAffiliateProfile() {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    // This delay is aimed at waiting for affiliate profile creation.
    yield delay(2000);
    // Which happens synchronously in external services.
    const { data } = yield call(() => userRoutes.userGet("affiliate-profile"));
    const user: User = yield selectState<User>((state) => state.user.data);
    yield put(userFetchSuccess({ ...user, affiliateProfile: data }));
  } catch (e) {
    const exception: any = e;
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    } else if (exception?.message === ErrorEvents.ERR_NETWORK) {
      event = ErrorEvents.ERR_NETWORK;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

function* getPromoCodes() {
  try {
    yield put(userPending());
    yield put(cleanUserError());
    const { data } = yield call(() => userRoutes.userGet("customer/promocodes"));
    yield put(promoCodesFetchSuccess(data));
  } catch (e) {
    const exception: any = e;
    toastError("Error.", "Could not get affiliate link.");
    let event: string | undefined;
    if (exception?.response?.status === 404) {
      event = ErrorEvents.NOT_FOUND;
    } else if (exception?.response?.status === 400) {
      event = ErrorEvents.BAD_REQUEST;
    } else if (exception?.response?.status === 401) {
      event = ErrorEvents.ACCESS_DENIED;
    } else if (exception?.message === ErrorEvents.ERR_NETWORK) {
      event = ErrorEvents.ERR_NETWORK;
    }
    yield put(
      userError({
        event,
        message: exception?.response?.data?.message,
      })
    );
  }
}

export default function* userSaga() {
  yield takeEvery(userActions.LOGIN, login);
  yield takeEvery(userActions.LOGOUT, logout);
  yield takeEvery(userActions.SIGNUP, signup);
  yield takeEvery(userActions.CREATE_PATIENT_PROFILE, createPatientProfile);
  yield takeEvery(userActions.SUBMIT_PHOTO_ID, submitPhotoId);
  yield takeEvery(userActions.GET_PAYMENT_METHODS, getPaymentMethods);
  yield takeEvery(userActions.ADD_PAYMENT_METHOD, addPaymentMethod);
  yield takeEvery(userActions.UPDATE_PAYMENT_METHOD, updatePaymentMethod);
  yield takeEvery(userActions.DELETE_PAYMENT_METHODS, deletePaymentMethods);
  yield takeEvery(userActions.SET_DEFAULT_PAYMENT_METHOD, setDefaultPaymentMethod);
  yield takeEvery(userActions.USER_UPDATE, userUpdate);
  yield takeEvery(userActions.UPDATE_SHIPPING_ADDRESS, updateShippingAddress);
  yield takeEvery(userActions.RESTORE_PASSWORD, restorePassword);
  yield takeEvery(userActions.COLLECT_MARKETING_EMAIL, collectMarketingEmail);
  yield takeEvery(userActions.SEND_TRAVEL_GROUP_EMAIL, sendTravelGroupEmail);
  yield takeEvery(userActions.GET_AFFILIATE_PROFILE, getAffiliateProfile);
  yield takeEvery(userActions.GET_PROMO_CODES, getPromoCodes);
  yield takeEvery(userActions.GET_ADDRESSES, getAddresses);
  yield takeEvery(userActions.CREATE_ADDRESS, createAddress);
  yield takeEvery(userActions.UPDATE_ADDRESS, updateAddress);
  yield takeEvery(userActions.DELETE_ADDRESS, deleteAddress);
}
